// The industry vocabulary members and Circles declare. One list, validated
// at every boundary (profile PATCH, Circle create/update, preferences
// modal) and mirrored by five CHECK constraints: users.industry_code,
// circles.industry_code, circle_profile_suggestions.industry_code,
// group_facts.suggested_industry_code and
// member_interest_industries.industry_code (all pinned by
// apps/server/__unit__/industry-vocabulary-sync.test.ts): changing a code is
// a migration that rewrites all five. Labels for the UI
// live in apps/web/messages/*/members.json under `industries.<code>`; the
// English label here backs `ProfileView.industry` (the derived label kept
// until the column drops) and the search embedding text, plus the legacy
// backfill below.
import { z } from 'zod';
export const INDUSTRY_CODES = [
    'technology',
    'finance',
    'healthcare',
    'education',
    'government',
    'media',
    'consulting',
    'retail',
    'manufacturing',
    'nonprofit',
    'real_estate',
    'legal',
    'marketing_advertising',
    'hospitality',
    'energy',
    'transportation_logistics',
    'arts_entertainment',
    'construction',
    'agriculture',
    'other',
];
export const IndustryCodeSchema = z.enum(INDUSTRY_CODES);
const INDUSTRY_CODE_SET = new Set(INDUSTRY_CODES);
export function isIndustryCode(value) {
    return INDUSTRY_CODE_SET.has(value);
}
// The industries a member can pick as interests in their Circle preferences:
// the vocabulary without "Other", which names no industry to be interested
// in. Mirrored by member_interest_industries_industry_code_check (pinned by
// apps/server/__unit__/industry-vocabulary-sync.test.ts).
export const InterestIndustryCodeSchema = IndustryCodeSchema.exclude(['other']);
export const INTEREST_INDUSTRY_CODES = InterestIndustryCodeSchema.options;
// A member's own interest tags: free text, at most MAX_INTEREST_TAGS, each
// 1..MAX_INTEREST_TAG_LENGTH characters once normalized, counted in code
// points as Postgres char_length counts them.
export const MAX_INTEREST_TAGS = 5;
export const MAX_INTEREST_TAG_LENGTH = 40;
// The tag as stored and shown: NFC, trimmed, inner whitespace runs collapsed
// to one space.
export function normalizeInterestTag(raw) {
    return raw.normalize('NFC').trim().replace(/\s+/gu, ' ');
}
// Tags compare case-insensitively: two tags with one key are one tag. The
// server stores it as member_interest_tags.label_key.
export function interestTagKey(raw) {
    return normalizeInterestTag(raw).toLowerCase();
}
// The validation code a Circle write answers with (under
// `details.industrySpecific`) when it is marked for one industry only without
// an industry. The web form matches on it to show the error on the checkbox.
export const INDUSTRY_SPECIFIC_NEEDS_INDUSTRY = 'industry_specific_requires_industry';
export const INDUSTRY_LABELS_EN = {
    technology: 'Technology',
    finance: 'Finance',
    healthcare: 'Healthcare',
    education: 'Education',
    government: 'Government',
    media: 'Media',
    consulting: 'Consulting',
    retail: 'Retail',
    manufacturing: 'Manufacturing',
    nonprofit: 'Non-profit',
    real_estate: 'Real estate',
    legal: 'Legal',
    marketing_advertising: 'Marketing & advertising',
    hospitality: 'Hospitality',
    energy: 'Energy',
    transportation_logistics: 'Transportation & logistics',
    arts_entertainment: 'Arts & entertainment',
    construction: 'Construction',
    agriculture: 'Agriculture',
    other: 'Other',
};
// The 11 labels the web offered before the vocabulary, which users.industry
// and old directory filter URLs can still hold. The server's
// backfill-industry-codes script maps them (`via: 'label'`).
export const LEGACY_INDUSTRY_LABELS = {
    Technology: 'technology',
    Finance: 'finance',
    Healthcare: 'healthcare',
    Education: 'education',
    Government: 'government',
    Media: 'media',
    Consulting: 'consulting',
    Retail: 'retail',
    Manufacturing: 'manufacturing',
    'Non-profit': 'nonprofit',
    Other: 'other',
};
// Plain-object indexing is unsafe on free text (`'constructor'`, `'__proto__'`
// resolve through the prototype chain rather than failing a lookup), so both
// tables below are Maps built once at module load.
const LEGACY_LABEL_LOOKUP = new Map(Object.entries(LEGACY_INDUSTRY_LABELS));
export function legacyIndustryLabelToCode(label) {
    return LEGACY_LABEL_LOOKUP.get(label) ?? null;
}
function normalize(text) {
    return text
        .normalize('NFKD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}
// True synonyms: free text seen on the account settings page and the admin
// editor that doesn't already normalize to one of the vocabulary's own codes
// or English labels (those are added below). Includes a few non-English
// forms seen in the dev DB (French/Spanish labels that slipped in via the
// old free-text field).
const SYNONYMS = {
    software: 'technology',
    tech: 'technology',
    ai: 'technology',
    consumertech: 'technology',
    edtech: 'technology',
    climatetech: 'technology',
    wellnesstech: 'technology',
    it: 'technology',
    tecnologia: 'technology',
    technologie: 'technology',
    fintech: 'finance',
    financialservices: 'finance',
    banking: 'finance',
    insurance: 'finance',
    finanzas: 'finance',
    pharmaceuticals: 'healthcare',
    pharma: 'healthcare',
    biotech: 'healthcare',
    medical: 'healthcare',
    salud: 'healthcare',
    sante: 'healthcare',
    nonprofits: 'nonprofit',
    communitynonprofit: 'nonprofit',
    ngo: 'nonprofit',
    notforprofit: 'nonprofit',
    charity: 'nonprofit',
    philanthropy: 'nonprofit',
    publicsector: 'government',
    logistics: 'transportation_logistics',
    transportation: 'transportation_logistics',
    realestate: 'real_estate',
    law: 'legal',
    marketing: 'marketing_advertising',
    advertising: 'marketing_advertising',
    entertainment: 'arts_entertainment',
    arts: 'arts_entertainment',
    cpg: 'retail',
    educacion: 'education',
    energie: 'energy',
};
const SYNONYM_LOOKUP = new Map([
    ...INDUSTRY_CODES.map((code) => [normalize(code), code]),
    ...INDUSTRY_CODES.map((code) => [
        normalize(INDUSTRY_LABELS_EN[code]),
        code,
    ]),
    ...Object.entries(SYNONYMS),
]);
export function legacyIndustryToCode(text) {
    if (text === null)
        return null;
    const trimmed = text.trim();
    if (trimmed === '')
        return null;
    const byLabel = LEGACY_LABEL_LOOKUP.get(trimmed);
    if (byLabel !== undefined)
        return { code: byLabel, via: 'label' };
    const bySynonym = SYNONYM_LOOKUP.get(normalize(trimmed));
    if (bySynonym !== undefined)
        return { code: bySynonym, via: 'synonym' };
    return { code: 'other', via: 'fallback' };
}
//# sourceMappingURL=industries.js.map